// Libraries
import { Configuration, GeographyApi } from "@esyoil-gmbh/geography-client";
import axios from "axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import {
  PelletsChartDataState,
  PelletsHistoryChartPayload,
} from "~/types/Charts";
import {
  ConsumptionOilRequestData,
  ConsumptionOilRequirement,
} from "~/types/HeatingOilCalculator";
import { PartnerType } from "~/types/Partner";
import { AverageCityPelletPrices } from "~/types/Pellets";
import {
  buildAlignedPelletsChartState,
  clonePelletsChartState,
  coercePelletsChartMonths,
  PelletsChartMonths,
} from "~/utils/pelletsChart";
import { fetchPelletsChartWithCache } from "~/utils/pelletsChartFetch";
import { createCityPriceModel } from "~/utils/priceUtils";
import { returnDateOnIntersections } from "../operations/store-helper-functions";
import { CityPriceModel } from "../types/Location";
dayjs.extend(utc);
dayjs.extend(duration);

const config = new Configuration({
  basePath: process.env.BACKBONE_ESYOIL,
});
const geoApi = new GeographyApi(config);

// Type definitions
interface DistrictPriceData {
  name: string;
  nutsCode: string;
  price: number;
  change: number;
  maxPrice24Month: number;
  minPrice24Month: number;
}

interface PelletsDistrictPrices {
  loose: DistrictPriceData[];
  pallet: DistrictPriceData[];
  bigbag: DistrictPriceData[];
}

@Module({
  name: "oilprice",
  namespaced: true,
  stateFactory: true,
})
export default class Oilprice extends VuexModule {
  IndexedCities: Array<string> = [
    "Hamburg",
    "Düsseldorf",
    "München",
    "Rostock",
    "Hannover",
    "Berlin",
    "Leipzig",
    "Dresden",
    "Frankfurt",
    "Karlsruhe",
    "Stuttgart",
  ];

  assets = {
    reletadUnits: "",
    labels: [],
    data: [],
  };

  historyChartData = {
    labels: [],
    data: [],
  };

  pelletsHistoryChartData: PelletsChartDataState = {
    labels: [],
    loose: { data: [] },
    pallet: { data: [] },
  };

  pelletsHistoryChartDataCache: Record<string, PelletsChartDataState> = {};

  pendingPelletsHistoryKey: string | null = null;

  countyLinks = [];
  cityLinks = [];
  topStateCities = [];
  stockMarketData = [];
  districtPrices: DistrictPriceData[] = [];
  pelletsDistrictPrices: PelletsDistrictPrices = {
    loose: [],
    pallet: [],
    bigbag: [],
  };

  oilRequirements = null;
  // Pellet specific requirements (separate from heating oil)
  pelletsRequirements: any = null;

  cityPlaces = [];

  cityHistoryChartData = {
    labels: [],
    data: [],
  };

  stateAssetsPrice = {
    name: "",
    currentPrice: { value: 0, changedAtUnix: "" },
    lastPrice: { value: 0, changedAtUnix: "" },
    relativeChange: 0,
  };

  pelletsStateAssetsPrice = {
    loose: {
      currentPrice: { value: 0, changedAtUnix: "" },
      lastPrice: { value: 0, changedAtUnix: "" },
      relativeChange: 0,
    },
    pallet: {
      currentPrice: { value: 0, changedAtUnix: "" },
      lastPrice: { value: 0, changedAtUnix: "" },
      relativeChange: 0,
    },
  };

  statesOilPriceHistory = {
    reletadUnits: "€ / 100 L",
    labels: [],
    sateOilPriceData: [],
    nationalOiPriceData: [],
  };

  stateArticle = {
    imageUrl: "",
    intro: [],
    content: [],
  };

  cheapestCities = [];

  cheapestCounties = [];

  consumptionOilRequirement = {
    literRequirement: 0,
    annualPrice: 0,
    monthlyPrice: 0,
  };

  averageCityPrices = {
    dates: {
      actual: null,
      previous: null,
    },
    priceData: [],
  };

  averageCityPelletPrices: AverageCityPelletPrices = {
    dates: {
      actual: null,
      previous: null,
    },
    loose: {
      priceData: [] as CityPriceModel[],
    },
    pallet: {
      priceData: [] as CityPriceModel[],
    },
    bigbag: {
      priceData: [] as CityPriceModel[],
    },
  };

  energyClassConsumption = [
    { value: 1.5, name: "A+" },
    { value: 3, name: "A" },
    { value: 4, name: "B" },
    { value: 7.5, name: "C" },
    { value: 11.5, name: "D" },
    { value: 14.5, name: "E" },
    { value: 18, name: "F" },
    { value: 22.5, name: "G" },
    { value: 30, name: "H" },
  ];

  @Mutation
  clearAssets() {
    this.assets.reletadUnits = "";
    this.assets.data = [];
  }

  @Mutation
  setConsumtionOilRequirement(data: ConsumptionOilRequirement) {
    this.consumptionOilRequirement = { ...data };
  }

  @Mutation
  pushStockMarketData(assetsData) {
    // this.stockMarketData = [];
    this.stockMarketData = assetsData;
  }

  @Mutation
  setCityPlacesData(assetsData) {
    this.cityPlaces = assetsData;
  }
  // Restructure city historical price data from the current and previous five months
  @Mutation
  restructureCityHistoryChartData(assetsData) {
    // Property IDs to extract data for
    const propertyIds = [1000, 4000, 5000, 8000, 10000];

    const extractedData = [];
    const uniqueDateTimestamps = new Map<string, number>();

    // Pre-compute boundaries as unix timestamps for avoiding repeated calls
    const currentDate = dayjs();
    const rangeStartUnix = currentDate
      .subtract(5, "months")
      .startOf("month")
      .unix();
    const rangeEndUnix = currentDate.endOf("month").unix();

    const formatTimestamp = (ts: number): string => {
      // Add 5 hours offset (matching original dayjs.unix(x).utc().add(5, "hour"))
      const d = new Date((ts + 5 * 3600) * 1000);
      const day = String(d.getUTCDate()).padStart(2, "0");
      const month = String(d.getUTCMonth() + 1).padStart(2, "0");
      const year = d.getUTCFullYear();
      return `${day}.${month}.${year}`;
    };

    for (let p = 0; p < propertyIds.length; p++) {
      const propertyId = propertyIds[p];
      const series = assetsData[propertyId];
      if (!series) {
        extractedData.push([]);
        continue;
      }

      const filteredData = [];
      for (let i = 0; i < series.length; i++) {
        const dataPoint = series[i];
        if (dataPoint.x !== undefined) {
          if (dataPoint.x > rangeStartUnix && dataPoint.x < rangeEndUnix) {
            filteredData.push(dataPoint);
            const label = formatTimestamp(dataPoint.x);
            if (!uniqueDateTimestamps.has(label)) {
              uniqueDateTimestamps.set(label, dataPoint.x);
            }
          }
        }
      }
      extractedData.push(filteredData);
    }

    // Sort by pre-stored timestamp without paring date strings
    const sortedDateLabels = Array.from(uniqueDateTimestamps.entries())
      .sort((a, b) => a[1] - b[1])
      .map((entry) => entry[0]);

    this.cityHistoryChartData.data = extractedData;
    this.cityHistoryChartData.labels = sortedDateLabels;
  }

  @Mutation
  restructureAndPushAssetsData(assetsData) {
    this.assets.reletadUnits = `${assetsData.unit.currency} / ${assetsData.unit.currencyDenominator}`;

    const formatUnixUtc = (ts: number): string => {
      const d = new Date(ts * 1000);
      const day = String(d.getUTCDate()).padStart(2, "0");
      const month = String(d.getUTCMonth() + 1).padStart(2, "0");
      const year = d.getUTCFullYear();
      return `${day}.${month}.${year}`;
    };

    const seriesLength = assetsData.series.length;
    const lastSeriesDate = assetsData.series[seriesLength - 1].date;
    const prognosisEndDate = assetsData.trend.to.date;
    const additionalPrognosisDays = Math.round(
      (prognosisEndDate - lastSeriesDate) / 86400,
    );
    const totalLoopLength = seriesLength + additionalPrognosisDays;

    const lastSeriesDateMs = lastSeriesDate * 1000;
    const msPerDay = 86400000;

    const newData = [];

    for (let i = 0; i < totalLoopLength; i++) {
      if (i < seriesLength) {
        newData.push({
          changedAtUnix: assetsData.series[i].date,
          changedAtUtc: formatUnixUtc(assetsData.series[i].date),
          priceData: assetsData.series[i].price,
          pricePrognosisDataTop: returnDateOnIntersections(
            assetsData.series[i].date,
            assetsData.trend.from.date,
            assetsData.trend.from.priceRange[0],
          ),
          pricePrognosisDataBottom: returnDateOnIntersections(
            assetsData.series[i].date,
            assetsData.trend.from.date,
            assetsData.trend.from.priceRange[1],
          ),
          pricePrognosisStartDate: assetsData.trend.from.date,
        });
      } else {
        const offsetDays = i + 1 - seriesLength;
        const d = new Date(lastSeriesDateMs + offsetDays * msPerDay);
        const day = String(d.getUTCDate()).padStart(2, "0");
        const month = String(d.getUTCMonth() + 1).padStart(2, "0");
        const year = d.getUTCFullYear();

        newData.push({
          changedAtUnix: "",
          changedAtUtc: `${day}.${month}.${year}`,
          priceData: null,
          pricePrognosisDataTop: returnDateOnIntersections(
            i + 1,
            totalLoopLength,
            assetsData.trend.to.priceRange[0],
          ),
          pricePrognosisDataBottom: returnDateOnIntersections(
            i + 1,
            totalLoopLength,
            assetsData.trend.to.priceRange[1],
          ),
        });
      }
    }

    // Fallback, falls der letzte Eintrag kein Preis für den Trendkanal hat
    const lastEntry = newData[newData.length - 1];
    if (lastEntry && !lastEntry.pricePrognosisDataTop) {
      lastEntry.pricePrognosisDataTop = assetsData.trend.to.priceRange[0];
    }
    if (lastEntry && !lastEntry.pricePrognosisDataBottom) {
      lastEntry.pricePrognosisDataBottom = assetsData.trend.to.priceRange[1];
    }

    this.assets.data.push(...newData);
  }

  @Mutation
  restructureAndPushAssetsDataWithPrognosis(assetsData: {
    data: [number];
    range: Array<number | string>;
    name: string;
  }) {
    const firstDataEntryDate = dayjs.unix(assetsData.data[0][0]);
    const lastDataEntryDate = dayjs.unix(
      assetsData.data[assetsData.data.length - 1][0],
    );
    const lastPrognosisEntryDate = dayjs.unix(assetsData.range[1][0]);
    const totalOfDays = dayjs
      .duration(lastPrognosisEntryDate.diff(firstDataEntryDate))
      .asDays();
    const additionalPrognosisDays = dayjs
      .duration(lastPrognosisEntryDate.diff(lastDataEntryDate))
      .asDays();
    let relatedTotalOfDays = assetsData.data.length + additionalPrognosisDays;

    if (assetsData.name === "60 Monate")
      relatedTotalOfDays = assetsData.data.length + additionalPrognosisDays - 3;
    if (assetsData.name === "120 Monate")
      relatedTotalOfDays = assetsData.data.length + additionalPrognosisDays - 3;

    const totalLoopLength = relatedTotalOfDays;

    this.assets.reletadUnits = "€ / 100 L";

    for (let i = 0; i < totalLoopLength; i++) {
      if (assetsData.data.length > i) {
        const Element = {
          changedAtUnix: assetsData.data[i][0],
          changedAtUtc: dayjs
            .unix(assetsData.data[i][0])
            .utc()
            .format("DD.MM.YYYY"),
          priceData: assetsData.data[i][1],
          pricePrognosisDataTop: returnDateOnIntersections(
            assetsData.data[i][0],
            assetsData.range[0][0],
            assetsData.range[0][1],
          ),
          pricePrognosisDataBottom: returnDateOnIntersections(
            assetsData.data[i][0],
            assetsData.range[0][0],
            assetsData.range[0][2],
          ),
        };
        this.assets.data.push(Element);
      } else {
        if (assetsData.name === "60 Monate") i += 6;
        if (assetsData.name === "120 Monate") i += 10;
        const Element = {
          changedAtUnix: "",
          changedAtUtc: dayjs
            .unix(assetsData.data[assetsData.data.length - 1][0])
            .utc()
            .add(i + 1 - assetsData.data.length, "days")
            .format("DD.MM.YYYY"),
          priceData: null,
          pricePrognosisDataTop: returnDateOnIntersections(
            i + 1,
            totalLoopLength,
            assetsData.range[1][1],
          ),
          pricePrognosisDataBottom: returnDateOnIntersections(
            i + 1,
            totalLoopLength,
            assetsData.range[1][2],
          ),
        };
        this.assets.data.push(Element);
      }
    }

    // Fallback, falls der letzte Eintrag kein Preis für den Trendkanal hat
    if (!this.assets.data[this.assets.data.length - 1].pricePrognosisDataTop) {
      this.assets.data[this.assets.data.length - 1].pricePrognosisDataTop =
        assetsData.range[1][1];
    }
    if (
      !this.assets.data[this.assets.data.length - 1].pricePrognosisDataBottom
    ) {
      this.assets.data[this.assets.data.length - 1].pricePrognosisDataBottom =
        assetsData.range[1][2];
    }
  }

  @Action
  async fetchAssetsData(payload: {
    monthsQuantity: number;
    assetName: string;
  }) {
    this.context.commit("clearAssets");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.SIDE_ASSETS_PRICES}/${payload.assetName}/${payload.monthsQuantity}`,
      );
      this.context.commit("restructureAndPushAssetsData", result.data.data);
    } catch (error) {
      //
    }
  }

  @Action
  async fetchOilAssetsPrognosis(monthsQuantity: number) {
    try {
      this.context.commit("clearAssets");
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.CHARTS_JAHRESUEBERSICHT}/${monthsQuantity}`,
      );
      this.context.commit(
        "restructureAndPushAssetsDataWithPrognosis",
        result.data.data,
      );
    } catch (error) {
      //
    }
  }

  @Mutation
  setNationalPriceTrend(data) {
    this.stateAssetsPrice.currentPrice.value = data.priceToday;
    this.stateAssetsPrice.lastPrice.value = data.pricePreviousDay;
    this.stateAssetsPrice.relativeChange = data.tendency;
  }

  @Mutation
  setPelletsPriceTrend(data) {
    const nowUnix = String(Math.floor(Date.now() / 1000));
    for (const key of ["loose", "pallet"] as const) {
      const payload = data?.[key];
      if (!payload) continue;
      const product = this.pelletsStateAssetsPrice[key];
      product.currentPrice.value = payload.priceToday;
      product.lastPrice.value = payload.pricePreviousDay;
      product.relativeChange = payload.tendency;
      product.currentPrice.changedAtUnix =
        payload.modifiedAt !== undefined ? String(payload.modifiedAt) : nowUnix;
      if (payload.previousModifiedAt !== undefined) {
        product.lastPrice.changedAtUnix = String(payload.previousModifiedAt);
      }
    }
  }

  @Action
  async fetchNationalPriceTrend() {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.CALC_NATIONAL_AVERAGE}`,
      );
      const responseData = result.data.data;
      this.context.commit("setNationalPriceTrend", responseData[0]);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchPelletsPriceTrend() {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.CALC_NATIONAL_AVERAGE}?type=PARTNER_PELLET`,
      );
      const responseData = result.data.data;
      this.context.commit("setPelletsPriceTrend", responseData);
    } catch (error) {
      console.error(error);
    }
  }

  @Mutation
  clearDistrictPrices() {
    this.districtPrices = [];
  }

  @Mutation
  clearPelletsDistrictPrices() {
    this.pelletsDistrictPrices = {
      loose: [],
      pallet: [],
      bigbag: [],
    };
  }

  @Mutation
  setDistrictPrices(data: DistrictPriceData[]) {
    this.districtPrices = data;
  }

  @Mutation
  setPelletsDistrictPrices(data: PelletsDistrictPrices) {
    this.pelletsDistrictPrices = {
      loose: data.loose || [],
      pallet: data.pallet || [],
      bigbag: data.bigbag || [],
    };
  }

  @Action
  async fetchDistrictPriceTrend() {
    this.context.commit("clearDistrictPrices");
    try {
      const result = await axios({
        method: "get",
        url: `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_AVERAGE_PRICE}`,
        headers: {
          accept: "*/*",
        },
      });

      const responseData = result.data.data;
      if (Array.isArray(responseData)) {
        this.context.commit("setDistrictPrices", responseData);
      }
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchPelletsDistrictPriceTrend() {
    this.context.commit("clearPelletsDistrictPrices");
    try {
      const result = await axios({
        method: "get",
        url: `${process.env.API_ESYOIL}${process.env.FEDERAL_STATE_AVERAGE_PRICE}?type=PARTNER_PELLET`,
        headers: {
          accept: "*/*",
        },
      });

      const responseData = result.data.data;
      if (typeof responseData === "object") {
        this.context.commit("setPelletsDistrictPrices", {
          loose: responseData.loose || [],
          pallet: responseData.pallet || [],
          bigbag: responseData.bigbag || [],
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  @Mutation
  setCheapestCities(cheapestCities) {
    this.cheapestCities = cheapestCities;
  }

  @Mutation
  setCheapestCounties(cheapestCounties) {
    this.cheapestCounties = cheapestCounties;
  }

  @Mutation
  setOilRequirements(oilRequirements) {
    this.oilRequirements = oilRequirements;
  }

  @Mutation
  setPelletsRequirements(pelletsRequirements) {
    this.pelletsRequirements = pelletsRequirements;
  }

  @Action
  async fetchStockMarketData() {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.STOCK_MARKET_DATA}`,
      );
      this.context.commit("pushStockMarketData", result.data.data.priceData);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchCheapestCities() {
    try {
      const result = await axios.get(
        `${process.env.BACKBONE_ESYOIL}${process.env.CHEAPEST_CITIES}`,
      );
      this.context.commit("setCheapestCities", result.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchCheapestCounties() {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.CHEAPEST_COUNTIES}`,
      );
      this.context.commit("setCheapestCounties", result.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Mutation
  setAverageCityPrices(priceData) {
    this.averageCityPrices.priceData = [];
    this.averageCityPrices.dates.actual = priceData.dates.actual;
    this.averageCityPrices.dates.previous = priceData.dates.previous;
    this.IndexedCities.forEach((city) => {
      const cityPriceModel: CityPriceModel = {
        name: city,
        zipcode: priceData[city].zipcode,
        seoName: priceData[city].seoName,
        price: {
          actual: priceData[city][priceData.dates.actual],
          previous: priceData[city][priceData.dates.previous],
          maxPrice24Month: priceData[city].max,
          minPrice24Month: priceData[city].min,
          change:
            priceData[city][priceData.dates.actual] -
            priceData[city][priceData.dates.previous],
        },
      };
      this.averageCityPrices.priceData.push(cityPriceModel);
    });
  }

  @Mutation
  setPendingPelletsHistoryKey(key: string | null) {
    this.pendingPelletsHistoryKey = key;
  }

  @Mutation
  setPelletsHistoryPrices(payload: {
    cacheKey: string;
    data: PelletsHistoryChartPayload;
  }) {
    const next: PelletsChartDataState = buildAlignedPelletsChartState(
      payload.data,
    );

    if (process.client) {
      this.pelletsHistoryChartDataCache = {
        ...this.pelletsHistoryChartDataCache,
        [payload.cacheKey]: next,
      };
    }
    this.pelletsHistoryChartData = clonePelletsChartState(next);
  }

  @Mutation
  loadCachedPelletsHistoryPrices(cacheKey: string) {
    const cached = this.pelletsHistoryChartDataCache[cacheKey];
    if (cached) this.pelletsHistoryChartData = clonePelletsChartState(cached);
  }

  @Mutation
  setAveragePelletsCityPrices(data: {
    loose?: any;
    pallet?: any;
    bigbag?: any;
    dates: {
      actual: string | null;
      previous: string | null;
    };
  }) {
    // Set dates once for all pellet types
    this.averageCityPelletPrices.dates.actual = data.dates.actual;
    this.averageCityPelletPrices.dates.previous = data.dates.previous;

    // Process each pellet type if it exists in the data
    if (data.loose) {
      const looseData = this.averageCityPelletPrices.loose;
      looseData.priceData = [];
      this.IndexedCities.forEach((city) => {
        if (data.loose[city]) {
          looseData.priceData.push(
            createCityPriceModel(city, data.loose, data.dates),
          );
        }
      });
    }

    if (data.pallet) {
      const palletData = this.averageCityPelletPrices.pallet;
      palletData.priceData = [];
      this.IndexedCities.forEach((city) => {
        if (data.pallet[city]) {
          palletData.priceData.push(
            createCityPriceModel(city, data.pallet, data.dates),
          );
        }
      });
    }

    if (data.bigbag) {
      const bigbagData = this.averageCityPelletPrices.bigbag;
      bigbagData.priceData = [];
      this.IndexedCities.forEach((city) => {
        if (data.bigbag[city]) {
          bigbagData.priceData.push(
            createCityPriceModel(city, data.bigbag, data.dates),
          );
        }
      });
    }
  }

  @Action
  async fetchAverageCityPrices() {
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.CITY_PRICE_AVERAGE}`,
      );
      this.context.commit("setAverageCityPrices", result.data.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchAveragePelletCityPrices() {
    const url = `${process.env.API_ESYOIL}${process.env.CITY_PRICE_AVERAGE}?type=PARTNER_PELLET`;

    try {
      const result = await axios.get(url);
      const responseData = result.data.data;

      if (typeof responseData === "object" && responseData !== null) {
        const { loose, pallet, bigbag } = responseData;

        const dates = loose?.dates ||
          pallet?.dates ||
          bigbag?.dates || {
            actual: null,
            previous: null,
          };

        this.context.commit("setAveragePelletsCityPrices", {
          loose,
          pallet,
          bigbag,
          dates,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  @Mutation
  setHeatingOilHistoryPrices(assetsData: Array<Array<number>>) {
    this.assets.data = [];
    this.assets.labels = [];
    this.assets.reletadUnits = "€ / 100 L";
    assetsData.forEach((intersection) => {
      this.assets.data.push({
        x: intersection[0],
        y: intersection[1],
      });
      this.assets.labels.push(dayjs(intersection[0]).format("DD.MM.YYYY"));
    });
  }

  @Action
  async fetchHeatingOilHistoryPrices(monthsQuantity: number) {
    this.context.commit("clearAssets");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.CHARTS_JAHRESUEBERSICHT}/${monthsQuantity}`,
      );
      this.context.commit("setHeatingOilHistoryPrices", result.data.data.data);
    } catch (error) {
      console.error(error);
    }
  }

  @Action
  async fetchPelletsHistoryPrices(payload: {
    monthsQuantity: PelletsChartMonths;
  }) {
    const monthsQuantity = coercePelletsChartMonths(payload.monthsQuantity);
    const cacheKey = `national:${monthsQuantity}`;
    await fetchPelletsChartWithCache(this.context, {
      url: `${process.env.API_ESYOIL}${process.env.CHARTS_JAHRESUEBERSICHT}/${monthsQuantity}?type=PARTNER_PELLET`,
      cacheKey,
      getCached: () => this.pelletsHistoryChartDataCache[cacheKey],
      getPendingKey: () => this.pendingPelletsHistoryKey,
      setPendingMutation: "setPendingPelletsHistoryKey",
      loadCachedMutation: "loadCachedPelletsHistoryPrices",
      setDataMutation: "setPelletsHistoryPrices",
      logScope: "oilprice/fetchPelletsHistoryPrices",
    });
  }

  @Mutation
  restructureAndPushAssetsHistoryData(assetsData) {
    this.historyChartData.labels = [];
    this.historyChartData.data = [];
    const Year0 = [];
    const Year1 = [];
    const Year2 = [];
    const Year3 = [];
    assetsData.year0.forEach((intersection) => {
      Year0.push({
        x: intersection[0],
        y: Number(intersection[1]),
      });
    });
    assetsData.year1.forEach((intersection) => {
      Year1.push({
        x: intersection[0],
        y: Number(intersection[1]),
      });
    });
    assetsData.year2.forEach((intersection) => {
      Year2.push({
        x: intersection[0],
        y: Number(intersection[1]),
      });
    });
    assetsData.year3.forEach((intersection) => {
      Year3.push({
        x: intersection[0],
        y: Number(intersection[1]),
      });
      this.historyChartData.labels.push(
        dayjs.unix(intersection[0]).utc().format("DD.MM.YYYY"),
      );
    });
    this.historyChartData.data = [Year0, Year1, Year2, Year3];
  }
  @Action
  async fetchHistoricalOilPrices() {
    this.context.commit("clearAssets");
    try {
      const result = await axios.get(
        `${process.env.API_ESYOIL}${process.env.YEARS_COMPARSION_CHART}`,
      );
      this.context.commit(
        "restructureAndPushAssetsHistoryData",
        result.data.data,
      );
    } catch (error) {
      //
    }
  }

  @Action
  async fetchCityPlacesData(payload: {
    cityName: string;
    zipcode: string;
    partnerType?: PartnerType;
  }) {
    this.context.commit("setCityPlacesData", []);
    try {
      const result =
        await geoApi.geographyV1GetPlacesInMunicipalityPlaceZipcodeLimitGet(
          payload.cityName,
          payload.zipcode,
          50,
          payload.partnerType,
        );
      this.context.commit("setCityPlacesData", result.data);
    } catch (error) {
      console.log(error);
    }
  }

  @Action
  async fetchCityHistoryChartData(zipcode: string) {
    try {
      const result = await axios.get(
        `${process.env.BACKBONE_ESYOIL}/heating-oil-calculator/v1/history/chart/amount/${zipcode}`,
      );
      this.context.commit("restructureCityHistoryChartData", result.data);
    } catch (error) {
      console.log("error", error);
    }
  }

  @Action
  async fetchConsumptionOilRequirement(
    data: ConsumptionOilRequestData,
  ): Promise<void> {
    try {
      const result = await axios.post<ConsumptionOilRequirement>(
        `${process.env.BACKBONE_ESYOIL}${process.env.OIL_CONSUMPTION_REQUIREMENT}`,
        data,
      );
      this.context.commit("setConsumtionOilRequirement", result.data);
    } catch (error) {
      console.log("error", error);
    }
  }

  @Action
  async fetchConsumptionPelletsRequirement(
    data: ConsumptionOilRequestData,
  ): Promise<void> {
    try {
      const result = await axios.post<ConsumptionOilRequirement>(
        `${process.env.BACKBONE_ESYOIL}${process.env.OIL_CONSUMPTION_REQUIREMENT}?type=PARTNER_PELLET`,
        data,
      );
      this.context.commit("setConsumtionOilRequirement", result.data);
    } catch (error) {
      console.log("error", error);
    }
  }

  @Action
  async fetchOilRequirements(data) {
    try {
      const result = await axios.get(
        `${process.env.BACKBONE_ESYOIL}${process.env.OIL_REQUIREMENT}/${data.zipCode}/${data.oilPrice}`,
      );
      this.context.commit("setOilRequirements", result.data);
    } catch (error) {
      console.log("error", error);
    }
  }

  @Action
  async fetchPelletsRequirements(data: {
    zipCode: string;
    pelletsPrice: number;
  }) {
    try {
      const result = await axios.get(
        `${process.env.BACKBONE_ESYOIL}${process.env.OIL_REQUIREMENT}/${
          data.zipCode
        }/${data.pelletsPrice * 2}?type=PARTNER_PELLET`,
      );
      this.context.commit("setPelletsRequirements", result.data);
    } catch (error) {
      console.log("error", error);
    }
  }
}
